import { parseISO, isValid } from 'date-fns';

/**
 * Utilitário para cálculo da Taxa de Inadimplência
 * 
 * Regras de Negócio:
 * 1. "Todos os Anos": Últimos 12 meses de todas as parcerias com status "em_debito"
 * 2. "Ano Atual": Últimos 12 meses do ano atual com status "em_debito"
 * 3. "Anos Anteriores (fechados)": Todas as parcerias do ano com status "em_debito"
 */

/**
 * Calcula a taxa de inadimplência baseada no filtro de ano selecionado
 * 
 * @param {Array} partnerships - Array de todas as parcerias
 * @param {string} selectedYear - Ano selecionado ('Todos os Anos', '2025', '2024', etc)
 * @returns {string|null} - Taxa em porcentagem (ex: "15.5") ou null se não aplicável
 */
export function calculateDefaultRate(partnerships, selectedYear) {
  if (!partnerships || partnerships.length === 0) {
    return '0.0';
  }

  const currentYear = new Date().getFullYear().toString();
  
  // Determinar qual cálculo aplicar
  if (selectedYear === 'Todos os Anos') {
    return calculateLast12MonthsRate(partnerships);
  } else if (selectedYear === currentYear) {
    return calculateCurrentYearLast12MonthsRate(partnerships, currentYear);
  } else {
    // Anos anteriores (fechados)
    return calculateClosedYearRate(partnerships, selectedYear);
  }
}

/**
 * Calcula taxa dos últimos 12 meses de TODAS as parcerias
 * Usado quando "Todos os Anos" está selecionado
 */
function calculateLast12MonthsRate(partnerships) {
  const now = new Date();
  const elevenMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  
  // Filtrar parcerias dos últimos 12 meses
  const recentPartnerships = partnerships.filter(p => {
    const createdDate = getCreatedDate(p);
    return createdDate && isValid(createdDate) && createdDate >= elevenMonthsAgo;
  });
  
  if (recentPartnerships.length === 0) {
    return '0.0';
  }
  
  // Contar apenas status 'em_debito'
  const inDefault = recentPartnerships.filter(p => p.status === 'em_debito').length;
  
  return ((inDefault / recentPartnerships.length) * 100).toFixed(1);
}

/**
 * Calcula taxa dos últimos 12 meses do ANO ATUAL
 * Usado quando o ano atual está selecionado
 */
function calculateCurrentYearLast12MonthsRate(partnerships, currentYear) {
  const now = new Date();
  const elevenMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  
  // Filtrar parcerias dos últimos 12 meses do ano atual
  const recentPartnerships = partnerships.filter(p => {
    const createdDate = getCreatedDate(p);
    return createdDate && 
           isValid(createdDate) && 
           createdDate >= elevenMonthsAgo && 
           p.ano?.toString() === currentYear;
  });
  
  if (recentPartnerships.length === 0) {
    return '0.0';
  }
  
  // Contar apenas status 'em_debito'
  const inDefault = recentPartnerships.filter(p => p.status === 'em_debito').length;
  
  return ((inDefault / recentPartnerships.length) * 100).toFixed(1);
}

/**
 * Calcula taxa de TODAS as parcerias de um ano FECHADO
 * Usado quando anos anteriores estão selecionados (2024, 2023, etc)
 */
function calculateClosedYearRate(partnerships, selectedYear) {
  // Filtrar todas as parcerias do ano fechado
  const yearPartnerships = partnerships.filter(p => 
    p.ano?.toString() === selectedYear
  );
  
  if (yearPartnerships.length === 0) {
    return '0.0';
  }
  
  // Contar apenas status 'em_debito'
  const inDefault = yearPartnerships.filter(p => p.status === 'em_debito').length;
  
  return ((inDefault / yearPartnerships.length) * 100).toFixed(1);
}

/**
 * Extrai a data de criação da parceria (suporta created_at ou createdAt)
 */
function getCreatedDate(partnership) {
  if (!partnership.created_at && !partnership.createdAt) {
    return null;
  }
  
  const dateValue = partnership.created_at || partnership.createdAt;
  
  if (typeof dateValue === 'string') {
    return parseISO(dateValue);
  }
  
  return new Date(dateValue);
}

/**
 * Retorna a descrição do período analisado
 * 
 * @param {string} selectedYear - Ano selecionado
 * @returns {string} - Descrição do período
 */
export function getDefaultRatePeriodDescription(selectedYear) {
  const currentYear = new Date().getFullYear().toString();
  
  if (selectedYear === 'Todos os Anos') {
    return 'Últimos 12 meses (em débito)';
  } else if (selectedYear === currentYear) {
    return 'Últimos 12 meses (em débito)';
  } else {
    return `Ano ${selectedYear} (em débito)`;
  }
}

/**
 * Verifica se o cálculo deve ser exibido para o ano selecionado
 * Sempre retorna true, pois agora calculamos para todos os cenários
 * 
 * @param {string} selectedYear - Ano selecionado
 * @returns {boolean} - Sempre true
 */
export function shouldShowDefaultRate(selectedYear) {
  return true; // Sempre mostrar, mas com cálculo diferente
}

/**
 * Retorna informações detalhadas sobre o cálculo
 * Útil para tooltips e documentação
 * 
 * @param {string} selectedYear - Ano selecionado
 * @returns {Object} - Informações sobre o cálculo
 */
export function getDefaultRateInfo(selectedYear) {
  const currentYear = new Date().getFullYear().toString();
  
  if (selectedYear === 'Todos os Anos') {
    return {
      title: 'Taxa de Inadimplência',
      period: 'Últimos 12 meses',
      description: 'Analisa os últimos 12 meses (mês atual + 11 meses anteriores) de todas as parcerias',
      formula: '(Parcerias em Débito ÷ Total de Parcerias) × 100',
      filter: 'Apenas parcerias com status "Em Débito"'
    };
  } else if (selectedYear === currentYear) {
    return {
      title: 'Taxa de Inadimplência',
      period: 'Últimos 12 meses do ano atual',
      description: 'Analisa os últimos 12 meses (mês atual + 11 meses anteriores) do ano atual',
      formula: '(Parcerias em Débito ÷ Total de Parcerias) × 100',
      filter: 'Apenas parcerias com status "Em Débito" do ano atual'
    };
  } else {
    return {
      title: 'Taxa de Inadimplência',
      period: `Ano ${selectedYear} (fechado)`,
      description: `Analisa todas as parcerias do ano ${selectedYear}`,
      formula: '(Parcerias em Débito ÷ Total de Parcerias) × 100',
      filter: `Apenas parcerias com status "Em Débito" do ano ${selectedYear}`
    };
  }
}
